import React, { useState, Fragment, useMemo } from 'react';
import { IconButton, Button, Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { Link } from 'react-router-dom';

import Title from '../../components/common/Title';
import { PUBLISHER_GRID_COLUMNS } from './utils';
import { PERMISSIONS, REMOVED } from '../../constants';
import { searchAnyFieldFilter } from 'utils/common';
import { ToolbarContainer } from 'components/common/Containers';
import { ActionButton } from 'components/common/Buttons';
import { useHasPermissions } from 'hooks/useHasPermissions';
import { useSelectionModel } from 'hooks/utils/useSelectionModel';
import {
  DataGridContainer,
  DataGridFilterField,
} from 'components/common/DataTables';
import {
  useGetPublishersListQuery,
  useUpdatePublisherStatusMutation,
} from '../../store/api/publisher.api';
import { useSnackbar } from 'notistack';

const STATUS_OPTS = [
  ['ACTIVE', 'activate'],
  ['PAUSED', 'pause'],
  ['REMOVED', 'remove'],
];
function QuickSearchToolbar(props) {
  return (
    <ToolbarContainer>
      <DataGridFilterField
        variant='standard'
        value={props.value}
        onChange={props.onChange}
        placeholder='Search…'
        InputProps={{
          startAdornment: <SearchIcon fontSize='small' />,
          endAdornment: (
            <IconButton
              title='Clear'
              aria-label='Clear'
              size='small'
              style={{ visibility: props.value ? 'visible' : 'hidden' }}
              onClick={props.clearSearch}
            >
              <ClearIcon fontSize='small' />
            </IconButton>
          ),
        }}
      />

      <Box flexGrow={1} display='flex' gap='2rem' px='1rem'>
        {STATUS_OPTS.map(([targetStatus, label]) => (
          <ActionButton
            key={targetStatus}
            disabled={props.disabled}
            onClick={props.onChangeStatus}
            variant='text'
            data-status={targetStatus}
            sx={{ textTransform: 'capitalize' }}
          >
            {label}
          </ActionButton>
        ))}
      </Box>

      <Button
        variant='contained'
        color='primary'
        component={Link}
        to='/publishers/create'
        disabled={props.disabled}
        sx={{ m: '5px 5px 0 0', whiteSpace: 'nowrap' }}
      >
        Add New
      </Button>
    </ToolbarContainer>
  );
}

export default function DataTable() {
  const canEditPublishers = useHasPermissions(
    PERMISSIONS.PUBLISHERS_MANAGEMENT,
  );
  const { enqueueSnackbar } = useSnackbar();
  const [searchText, setSearchText] = useState('');
  const { data = [] } = useGetPublishersListQuery();
  const [updateStatus] = useUpdatePublisherStatusMutation();
  const rows = useMemo(() => {
    const filterFn = searchText
      ? searchAnyFieldFilter(searchText)
      : ({ status }) => status !== REMOVED;
    return data.filter(filterFn);
  }, [data, searchText]);
  const { selectionModel, setSelectionModel, clearSelection, getSelectedIds } =
    useSelectionModel(data);

  const onChangeStatus = async (e) => {
    try {
      const ids = getSelectedIds();
      await updateStatus({
        status: e.currentTarget.dataset.status,
        ids,
      }).unwrap();
      clearSelection();
      enqueueSnackbar(`${ids.length} Publisher(s) updated`, {
        variant: 'success',
      });
    } catch (e) {
      enqueueSnackbar(`Failed to update publisher status`, {
        variant: 'error',
      });
    }
  };

  return (
    <Fragment>
      <Box>
        <Title>Publishers</Title>
      </Box>
      <DataGridContainer>
        <DataGrid
          rows={rows || []}
          columns={PUBLISHER_GRID_COLUMNS}
          checkboxSelection={canEditPublishers}
          disableRowSelectionOnClick
          disableColumnMenu
          hideFooterPagination
          rowSelectionModel={selectionModel}
          showToolbar
          onRowSelectionModelChange={setSelectionModel}
          getRowClassName={(params) =>
            params.row.status === 'removed' ? 'disabled' : ''
          }
          slots={{
            toolbar: QuickSearchToolbar,
          }}
          slotProps={{
            toolbar: {
              value: searchText,
              onChange: (event) => setSearchText(event.target.value),
              clearSearch: () => setSearchText(''),
              onChangeStatus: onChangeStatus,
              disabled: !canEditPublishers,
            },
          }}
        />
      </DataGridContainer>
    </Fragment>
  );
}
